import { useFormik } from "formik";
import type React from "react";
import { useTranslation } from "react-i18next";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useGroups } from "hooks/useGroups";
import { useModal } from "hooks/useModal";

import { TeacherModals } from "constants/modals-constants";

import { InputWithErrorMessage } from "components/InputWithErrorMessage";
import { MultiStepModal } from "components/MultiStepModal";
import { MultipleEmailInput } from "components/MultipleEmailInput/MultipleEmailInput";

import { Heading4 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { Box, CssFlex } from "styles/helpers/layout";

import { type NewGroupFormFields, buildNewGroupFormSchema } from "./CreateGroupModal.schema";

interface CreateGroupModalProps {
  onGroupCreated: () => void;
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ onGroupCreated }) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const { getStudentTermByDomain } = useDomainHandler();
  const { createGroup, setStudentsToAdd, addStudentsToGroup } = useGroups();

  const formik = useFormik<NewGroupFormFields>({
    initialValues: {
      name: "",
    },
    validationSchema: buildNewGroupFormSchema(),
    validateOnChange: true,
    onSubmit: async (values) => {
      // Call endpoint to create the group
      const groupId = await createGroup(values.name);

      // If group creation succeeded, attempt to add students to it
      if (groupId) {
        // If students were specified, attempt to add them to the group
        if (formik.values.studentEmails && formik.values.studentEmails.length > 0) {
          setStudentsToAdd(formik.values.studentEmails.join(" "));
          await addStudentsToGroup(groupId);
        }

        // Trigger callback function once we're done adding the group
        onGroupCreated();
      }

      // Reset form fields after submit is complete
      formik.setSubmitting(false);
      formik.resetForm();
      closeModal();
    },
  });

  const createGroupSteps = [
    {
      id: "group-details-step",
      label: t("groups:createGroupModal.steps.setGroupDetails.title"),
      iconName: "fas fa-users",
      content: (
        <form noValidate onSubmit={formik.handleSubmit}>
          <CssFlex flexDirection="column" gap={0.5}>
            <Box paddingTop={3}>
              <label htmlFor="create-group-modal-name-input">
                {t("groups:createGroupModal.steps.setGroupDetails.fields.name.label")}
              </label>
              <InputWithErrorMessage
                dataCy="group-name-input"
                errorMessage={formik.errors.name}
                hasError={!!(formik.errors.name && formik.touched.name)}
                id="create-group-modal-name-input"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
              />
            </Box>
          </CssFlex>
        </form>
      ),
      disableNextButton: !formik.dirty || !formik.isValid,
      nextButtonLabel: t("groups:createGroupModal.steps.setGroupDetails.buttonLabels.next"),
    },
    {
      id: "add-students-step",
      label: t("groups:createGroupModal.steps.addStudents.title", {
        studentTerm: getStudentTermByDomain({ usePlural: true }),
      }),
      iconName: "fas fa-paper-plane",
      content: (
        <CssFlex flexDirection="column" gap={0.5}>
          <Heading4>
            {t("groups:createGroupModal.steps.addStudents.title", {
              studentTerm: getStudentTermByDomain({ usePlural: true }),
            })}
          </Heading4>
          <ParagraphLarge>
            {t("groups:createGroupModal.steps.addStudents.description", {
              studentTerm: getStudentTermByDomain({ usePlural: true }),
            })}
          </ParagraphLarge>
          {/* TODO: Replace by dropdown where user can search for a student by mail, name or student number
              (action "fetchUsersByEducationsForTeacher" from "useProfile" can be used to fetch data for this) */}
          <MultipleEmailInput
            dataCy="group-student-multiple-email-input"
            onSelectedEmailsChanged={(emails) => {
              formik.setFieldValue(
                "studentEmails",
                emails.map(({ email }) => email),
              );
            }}
          />
        </CssFlex>
      ),
      nextButtonLabel: t("groups:createGroupModal.steps.addStudents.buttonLabels.next"),
      disableNextButton: !formik.dirty || !formik.isValid || formik.isSubmitting,
      onNextButtonClick: () => {
        formik.setSubmitting(true);
        formik.submitForm();
      },
    },
  ];

  return (
    <MultiStepModal
      id={TeacherModals.CreateGroup}
      title={t("groups:createGroupModal.title")}
      steps={createGroupSteps}
      onClose={() => formik.resetForm()}
      autoCloseOnLastNextButtonClick={false}
    />
  );
};
